import React, { useState, useEffect, useRef } from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { chatbotService } from '../../../../services/chatbotService';
import { renderChatMarkdown } from '../../../../utility/renderMarkdownText';
import { agentInstanceService, agentService } from '../../../../services/agentService';
import { Flag, FlagOff, Move } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../../../../components/ui/Modal';
import { convertMillisecondsToSeconds } from '../../../../utility/time';
import { cssColor } from '../../../../theme/engine';
import { Button } from '../../../ui/Button';
import { Checkbox, TextArea, TextField } from '../../../ui/Field';
import { Overlay } from '../../../ui/Overlay';
import { Surface } from '../../../ui/Surface';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';
import { SourcesBadge, SourcesDrawer } from './ResponseSources';

const CONVERSATIONS_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('en-GB', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return iso;
  }
};

type ConversationSummary = {
  id: string;
  tenantId?: string | null;
  chatbotId?: string | null;
  status?: string | null;
  title?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
  messageCount?: number;
  createdAt?: string;
  updatedAt?: string;
  avgResponseTime?: number; // in milliseconds
};

type ConversationMessage = {
  id: string;
  conversationId: string;
  senderType: 'user' | 'agent' | string;
  message: string;
  messageType?: string;
  createdAt: string;
};

interface ConversationsTabProps {
  chatbot: any;
}

export const VideoModal: React.FC<{
  videoSrc: string;
  onClose: () => void;
}> = ({ videoSrc, onClose }) => {
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Lock background scroll
  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  return (
    <Overlay className="fixed inset-0 z-[999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-[92%] max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Close Button */}
        <Button tone="custom"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 bg-white/90 hover:bg-white text-gray-800 rounded-full w-9 h-9 flex items-center justify-center shadow-md transition"
        >
          ✕
        </Button>

        <video
          src={videoSrc}
          controls
          autoPlay
          className="w-full h-full max-h-[80vh] bg-black"
        />
      </div>
    </Overlay>
  );
};

export const AssessmentCarousel: React.FC<{
  content: any[];
}> = ({ content }) => {
  const [activeVideo, setActiveVideo] = React.useState<string | null>(null);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // 🔥 Prevent vertical scroll when swiping horizontally
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.stopPropagation();
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: true });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, []);

  if (!content?.length) return null;

  return (
    <div className="mt-4">
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto overscroll-x-contain scroll-smooth snap-x snap-mandatory"
      >
        {content.map((item, index) => (
          <div
            key={index}
            className="max-w-[75%] flex-shrink-0 snap-start rounded-2xl border border-violet-200 bg-gradient-to-br from-white to-violet-50 p-5 shadow-sm"
          >
            {/* Text Content */}
            <div className="prose prose-sm max-w-none">
              {renderChatMarkdown(item.formattedText)}
            </div>

            {/* Divider */}
            <div className="my-4 h-px bg-violet-100" />

            {/* Video Section */}
            {item.metadata?.media?.length ? (
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">
                  Recommended Videos
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {item.metadata?.media.map((video: any, vidIndex: number) => (
                    <div
                      key={vidIndex}
                      onClick={() => setActiveVideo(video.url)}
                      className="group relative rounded-xl overflow-hidden bg-black aspect-video cursor-pointer"
                    >
                      <video
                        src={video.url}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        preload="metadata"
                      />

                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition" />

                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition">
                          <svg
                            className="w-5 h-5 text-violet-600 ml-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M6 4l10 6-10 6V4z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : ""}
          </div>
        ))}
      </div>
      {activeVideo && (
        <VideoModal
          videoSrc={activeVideo}
          onClose={() => setActiveVideo(null)}
        />
      )}
    </div>
  );
};

export const SymptomCheckerAssessmentMessageBubble: React.FC<{
  metadata: any;
}> = ({
  metadata,
}) => {
    const { isAssessment, ...restMetadata } = metadata || {};

    const topProbableConditions = (restMetadata?.topProbableConditions || [])
    const mostProbableCondition = topProbableConditions[0] || null;
    const otherPossibleConditions = topProbableConditions.slice(1) || [];

    const sliderRef = React.useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(true);

    const [activeVideo, setActiveVideo] = useState<string | null>(null);

    React.useEffect(() => {
      const el = sliderRef.current;
      if (!el) return;

      const updateScrollState = () => {
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(
          el.scrollLeft + el.clientWidth < el.scrollWidth - 5
        );
      };

      updateScrollState();
      el.addEventListener("scroll", updateScrollState);
      window.addEventListener("resize", updateScrollState);

      return () => {
        el.removeEventListener("scroll", updateScrollState);
        window.removeEventListener("resize", updateScrollState);
      };
    }, []);

    const scroll = (direction: "left" | "right") => {
      const el = sliderRef.current;
      if (!el) return;

      const scrollAmount = 320; // width of card + gap
      el.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    };

    return (
      <>
        {/* Initial Statement */}
        <div>
          <p className="text-base">
            Based on the above pattern, the following are the possible medical conditions for{" "}
            <strong>{restMetadata?.demographicsMessage || "Unknown demographics"}</strong>.
          </p>
        </div>

        {/* Most Probable Condition */}
        <div className="rounded-2xl border-2 border-c-0066cc bg-gradient-to-br from-white to-blue-50 p-6 shadow-md">

          <div className="flex justify-between items-start">
            <h2 className="text-lg font-bold text-c-0052a3">
              🏥 Most Probable Condition: {mostProbableCondition?.conditionName || "Unable to identify the most probable condition based on the provided information"}
            </h2>
            {(mostProbableCondition?.conditionDetailsLink || "").trim() !== "" ? (
              <a
                href={mostProbableCondition.conditionDetailsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm px-4 py-2 rounded-lg transition bg-c-0066cc hover:bg-c-0052a3 text-white"
              >
                Read More
              </a>
            ) : ""}
          </div>

          <div className="mt-4 space-y-4 text-sm text-gray-700">

            <div>
              <h4 className="font-semibold">📝 Description</h4>
              <p>
                {mostProbableCondition?.conditionSummary || "-"}
              </p>
            </div>

            <div>
              <h4 className="font-semibold">🧬 Causes</h4>
              <p>
                {mostProbableCondition?.causes || "-"}
              </p>
            </div>

            {/* Chief Complaint */}
            <div>
              <h4 className="font-semibold">📢 Chief Complaint</h4>
              <p>{restMetadata?.chiefComplaint || "None specified"}</p>
            </div>

            {/* Reported Symptoms */}
            <div>
              <h4 className="font-semibold">🤒 Your Reported Symptoms</h4>
              {restMetadata?.reportedSymptoms?.length ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {restMetadata.reportedSymptoms.map(
                    (symptom: string, index: number) => (
                      <span
                        key={index}
                        className='px-3 py-1 text-sm font-medium bg-gray-200 text-gray-800 rounded-full border border-gray-300 shadow-sm'
                      >
                        {symptom}
                      </span>
                    )
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-1">
                  No symptoms reported
                </p>
              )}
            </div>

            <div>
              <h4 className="font-semibold">🛡️ Recommended Precautions</h4>
              <ul className="list-disc list-inside mt-1 space-y-1">
                {mostProbableCondition?.precautionaryMeasures?.map((measure: string, index: number) => (
                  <li key={index}>{measure}</li>
                )) || <li>No specific precautions available</li>}
              </ul>
            </div>

            {mostProbableCondition?.treatmentRecommendations?.length ? (
              <div>
                <h4 className="font-semibold">💊 Treatment &amp; Management</h4>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {mostProbableCondition.treatmentRecommendations.map((rec: string, index: number) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            ) : ""}

            {mostProbableCondition?.homeRemedies?.length ? (
              <div>
                <h4 className="font-semibold">🏡 Home Remedies</h4>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {mostProbableCondition.homeRemedies.map((rem: string, index: number) => (
                    <li key={index}>{rem}</li>
                  ))}
                </ul>
              </div>
            ) : ""}

            {/* Recommended Videos */}
            {mostProbableCondition?.media?.length ? (
              <div>
                <h4 className="font-semibold">
                  🎥 Recommended Videos
                </h4>

                <div className="space-y-3">
                  {mostProbableCondition.media.map((video: any, index: number) => (
                    <Surface tone="card-hover"
                      key={index}
                      onClick={() => setActiveVideo(video.url)}
                      className="flex items-center gap-4 border rounded-xl p-3 transition cursor-pointer group"
                    >
                      {/* Left: Video Thumbnail */}
                      <div className="relative w-40 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-black">
                        <video
                          src={video.url}
                          preload="metadata"
                          className="w-full h-full object-cover"
                        />

                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition" />

                        {/* Play Icon */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow-md group-hover:scale-110 transition">
                            <svg
                              className="w-4 h-4 text-c-0066cc ml-0.5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M6 4l10 6-10 6V4z" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Right: Title */}
                      <div className="flex-1">
                        <h5 className="text-sm font-semibold text-gray-900 group-hover:text-c-0066cc transition">
                          {video.title || "Watch Video"}
                        </h5>
                        {video.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {video.description}
                          </p>
                        )}
                      </div>
                    </Surface>
                  ))}
                </div>
              </div>
            ) : null}

          </div>
        </div>

        {/* Other Possible Conditions Slider */}
        {otherPossibleConditions.length ? (
          <div className="relative">
            <h3 className="font-semibold text-gray-800 mb-3">
              🔍 Other Possible Conditions
            </h3>

            {/* Left Arrow */}
            {canScrollLeft && (
              <Button tone="custom"
                onClick={() => scroll("left")}
                className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 shadow-md w-9 h-9 rounded-full flex items-center justify-center hover:bg-c-0066cc hover:text-white transition"
              >
                ‹
              </Button>
            )}

            {/* Right Arrow */}
            {canScrollRight && (
              <Button tone="custom"
                onClick={() => scroll("right")}
                className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 shadow-md w-9 h-9 rounded-full flex items-center justify-center hover:bg-c-0066cc hover:text-white transition"
              >
                ›
              </Button>
            )}

            {/* Slider */}
            <div
              ref={sliderRef}
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 scrollbar-hide"
            >
              {otherPossibleConditions.map((condition: any, index: number) => (
                <Surface tone="card-hover"
                  key={index}
                  className="min-w-[280px] snap-start border rounded-xl p-4 flex flex-col justify-between transition"
                >
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      🏥 {condition.conditionName}
                    </h4>

                    <p className="text-sm text-gray-600 mt-2">
                      {condition.conditionSummary?.substring(0, 100) ||
                        "No summary available"}
                      ...
                    </p>
                  </div>

                  {(condition.conditionDetailsLink || "").trim() !== "" && (
                    <a
                      href={condition.conditionDetailsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-block text-center text-sm px-4 py-2 rounded-lg transition bg-c-0066cc hover:bg-c-0052a3 text-white"
                    >
                      Read More
                    </a>
                  )}
                </Surface>
              ))}
            </div>
          </div>
        ) : null}

        {/* Disclaimer */}
        <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 text-sm text-gray-700">
          <h4 className="font-semibold text-yellow-800">⚠️ Important Medical Disclaimer</h4>
          <p className="mt-2">
            {mostProbableCondition?.disclaimer || "The information provided here is for general informational purposes only and should not be considered medical advice. It is not a substitute for professional medical diagnosis, treatment, or care. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition."}
          </p>
        </div>

        {activeVideo && (
          <VideoModal
            videoSrc={activeVideo}
            onClose={() => setActiveVideo(null)}
          />
        )}
      </>
    )
  }

const ConversationsTab: React.FC<ConversationsTabProps> = ({ chatbot }) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<any | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Bumped on every fresh (page 1) load so responses from older requests are ignored.
  const listRequestRef = useRef(0);
  // Same idea for the open conversation, so a slow response can't reopen one that was cleared.
  const conversationRequestRef = useRef(0);
  const prevSearchRef = useRef('');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [showAuditMenu, setShowAuditMenu] = useState(false);
  const [showAuditQuestion, setShowAuditQuestion] = useState(false);
  const [auditTargetMessage, setAuditTargetMessage] = useState<any>(null);
  const [auditUserMessage, setAuditUserMessage] = useState<any>(null);
  /** Bot reply whose knowledge sources are open in the side panel (looked up by id so a refresh updates it). */
  const [sourcesMessageId, setSourcesMessageId] = useState<string | null>(null);
  const [agentInstances, setAgentInstances] = useState<any[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState<any>({});


  const openAuditMenu = async (message: any) => {
    console.log("Opening audit menu for message:", message);
    // find the last user message in the selectedConversation that occurs before the provided message
    const msgs = selectedConversation?.messages ?? [];
    let userMsg: any = null;
    const idx = msgs.findIndex((m: any) => m.id === message.id);

    if (idx >= 0) {
      for (let i = idx - 1; i >= 0; i--) {
        if (msgs[i]?.senderType === 'user') {
          userMsg = msgs[i];
          break;
        }
      }
    } else {
      // fallback: pick the latest user message with createdAt < message.createdAt
      const targetTime = message?.createdAt ? new Date(message.createdAt).getTime() : NaN;
      userMsg =
        [...msgs]
          .filter((m: any) => m?.senderType === 'user' && m?.createdAt && new Date(m.createdAt).getTime() < targetTime)
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null;
    }

    if (!userMsg) {
      toast.error("No preceding user message found for this agent response.");
      return;
    }
    setAuditUserMessage(userMsg);
    console.log('Selected audit user message:', userMsg);
    setAuditTargetMessage(message);

    setAudit({
      conversationId: message.conversationId,
      userMessageId: userMsg.id,
      agentMessageId: message.id,
      correctedAnswer: message.message,
      chatbotId: chatbot?.id,
      reason: "wrong response",
    })
    // setAuditUserMessage()
    try {
      const res: any = await agentInstanceService.listAgentInstance?.({
        page: 1,
        limit: 50,
        isActive: true,
        chatBotId: chatbot?.id,
      })
      const raw = (res && (res.data?.agentInstances || res.agentInstances || res.data || res)) || [];
      console.log("Fetched agentInstances for audit:", raw);
      setAgentInstances(raw ?? []);
      // setShowAuditMenu(true);
    } catch (err) {
      console.error("Failed to load agentInstances", err);
    }
  };

  const fetchConversationPage = (page: number) =>
    chatbotService.listConversationHistory(chatbot.id, {
      page,
      limit: CONVERSATIONS_PAGE_SIZE,
      search: debouncedSearch || undefined,
    });

  // Loads the first page, replacing the list (initial load, refresh, search change).
  // `resetSelection` closes the open conversation and opens the first result instead (used for search).
  const loadList = async ({ resetSelection = false }: { resetSelection?: boolean } = {}) => {
    const requestId = ++listRequestRef.current;
    setLoadMoreFailed(false);
    setLoadingMore(false);
    if (!chatbot?.id) {
      setConversations([]);
      setListPage(1);
      setListTotal(0);
      setHasMore(false);
      setSelectedId(null);
      setSelectedConversation(null);
      return;
    }
    if (resetSelection) {
      setSelectedId(null);
      setSelectedConversation(null);
    }
    setLoadingList(true);
    setError(null);
    try {
      const res: any = await fetchConversationPage(1);
      if (requestId !== listRequestRef.current) return;
      const records = res?.records ?? [];
      setConversations(records);
      setListPage(1);
      setListTotal(res?.total ?? records.length);
      setHasMore(1 < (res?.totalPages ?? 0));
      // auto-select first record if present
      if (resetSelection) {
        setSelectedId(records[0]?.id ?? null);
      } else if (records.length > 0) {
        // Keep the open conversation only if it's still in the reloaded list (e.g. not one from a later page).
        setSelectedId((prev) =>
          prev && records.some((r: ConversationSummary) => r.id === prev) ? prev : records[0].id
        );
      } else {
        setSelectedId(null);
        setSelectedConversation(null);
      }
    } catch (err: any) {
      if (requestId !== listRequestRef.current) return;
      console.error('Failed to load conversation list', err);
      setError('Failed to load conversations.');
    } finally {
      if (requestId === listRequestRef.current) setLoadingList(false);
    }
  };

  // Appends the next page when the user scrolls to the bottom of the list.
  const loadMore = async () => {
    if (!chatbot?.id || loadingList || loadingMore || !hasMore) return;
    const requestId = listRequestRef.current;
    const nextPage = listPage + 1;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    try {
      const res: any = await fetchConversationPage(nextPage);
      if (requestId !== listRequestRef.current) return;
      const records: ConversationSummary[] = res?.records ?? [];
      // A conversation can shift between pages when it receives a new message; skip ones already shown.
      setConversations((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...records.filter((r) => !seen.has(r.id))];
      });
      setListPage(nextPage);
      if (typeof res?.total === 'number') setListTotal(res.total);
      setHasMore(nextPage < (res?.totalPages ?? 0));
    } catch (err: any) {
      if (requestId !== listRequestRef.current) return;
      console.error('Failed to load more conversations', err);
      setLoadMoreFailed(true);
    } finally {
      if (requestId === listRequestRef.current) setLoadingMore(false);
    }
  };

  const listBottomRef = useInfiniteScroll(() => {
    if (hasMore && !loadMoreFailed) loadMore();
  }, loadingList || loadingMore);

  const submitMultiAudit = async (toBeTrained: boolean) => {
    if (!auditTargetMessage || selectedAgents.length === 0) return;

    try {
      setLoading(true);
      for (const agentId of selectedAgents) {
        const body = {
          ...audit,
          agentInstanceId: agentId,
          toBeTrained: toBeTrained,
        };

        await agentInstanceService.createConversationAudit(body);
      }
      setLoading(false);
      loadConversation();
      toast.success(`Audit submitted for ${selectedAgents.length} agents.`);


    } catch (err) {
      console.error(err);
      setLoading(false);
      toast.error("Failed to submit audit.");
    } finally {
      setLoading(false);
      setShowAuditMenu(false);
      setShowAuditQuestion(false);
      setAudit({});
      setSelectedAgents([]);
    }
  };

  const loadConversation = async () => {
    const requestId = ++conversationRequestRef.current;
    if (!selectedId) {
      setSelectedConversation(null);
      setLoadingConversation(false);
      return;
    }
    setLoadingConversation(true);
    setError(null);
    try {
      const conv: any = await chatbotService.singleConversationHistoryById(selectedId);
      if (requestId !== conversationRequestRef.current) return;
      // ensure messages are sorted by createdAt ascending
      const messages: any = (conv?.messages ?? [])
        .slice()
        .sort((a: ConversationMessage, b: ConversationMessage) => {
          const ta = new Date(a.createdAt).getTime();
          const tb = new Date(b.createdAt).getTime();
          return ta - tb;
        })
        .map((message: any) => ({
          ...message,
          metadata: message.metadata && typeof message.metadata === "string" ? JSON.parse(message.metadata) : message.metadata
        }));
      setSelectedConversation({ ...conv, messages });
    } catch (err: any) {
      if (requestId !== conversationRequestRef.current) return;
      console.error('Failed to load conversation', err);
      setError('Failed to load conversation messages.');
    } finally {
      if (requestId === conversationRequestRef.current) setLoadingConversation(false);
    }
  };





  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages, selectedId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // A different chatbot starts with a clear search.
  useEffect(() => {
    setSearchTerm('');
    setDebouncedSearch('');
  }, [chatbot?.id]);

  // Every new chatbot or search term starts again from page 1; a new search also reopens the first result.
  useEffect(() => {
    const searchChanged = prevSearchRef.current !== debouncedSearch;
    prevSearchRef.current = debouncedSearch;
    loadList({ resetSelection: searchChanged });
  }, [chatbot?.id, debouncedSearch]);

  // load detail (messages) when selectedId changes
  useEffect(() => {



    loadConversation();
  }, [selectedId]);

  return (
    <div className="p-6">
      <div className="grid grid-cols-10 gap-4 min-h-[520px]">
        {/* Sidebar */}
        <aside className="col-span-10 md:col-span-3 bg-agentic-gray-50 rounded-lg border p-3 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-agentic-text-secondary" />
              <h3 className="text-sm font-semibold text-agentic-text-primary">Conversations</h3>
            </div>
            <div className="text-xs text-agentic-text-secondary">
              {loadingList
                ? '...'
                : listTotal > conversations.length
                  ? `${conversations.length} of ${listTotal}`
                  : conversations.length}
            </div>
          </div>

          <TextField
            tone="default-focus-primary"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search conversations…"
            className="w-full mb-3 px-3 py-2 text-sm rounded-lg border bg-white focus:outline-none focus:ring-2"
          />

          <div className="h-[500px] overflow-y-auto overflow-x-hidden space-y-3">
            {loadingList ? (
              <div className="text-sm text-agentic-text-secondary">Loading conversations…</div>
            ) : conversations.length === 0 ? (
              <div className="text-sm text-agentic-text-secondary">
                {debouncedSearch ? 'No conversation found.' : 'No conversation history available.'}
              </div>
            ) : (
              conversations.map((c) => (
                <Button tone="custom"
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left p-3 rounded-lg flex items-start gap-3 transition-shadow ${c.id === selectedId ? 'bg-white border shadow' : 'hover:bg-white/60'
                    }`}
                >
                  <div className="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center bg-agentic-primary-blue text-white font-semibold text-sm">
                    {(c.title ?? 'U')
                      .split(' ')
                      .map((s) => s[0])
                      .slice(0, 2)
                      .join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-medium text-agentic-text-primary text-sm truncate">
                        {c.title ?? 'Conversation'}
                      </div>
                      <div className="text-xs text-agentic-text-secondary ml-2 whitespace-nowrap">
                        {formatDate(c.lastMessageAt ?? c.createdAt)}
                      </div>
                    </div>

                    <div
                      className="text-xs text-agentic-text-secondary mt-1"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {c.lastMessage ?? '—'}
                    </div>
                    <div className='mt-2 flex items-center justify-between '>
                      <div className="text-xs text-agentic-text-secondary mt-2">
                        Messages: {c.messageCount ?? 0}
                      </div>
                      <div className='text-xs text-agentic-text-secondary mt-2'>
                        Avg Time: {c.avgResponseTime ? convertMillisecondsToSeconds(c.avgResponseTime) : "-"}
                      </div>
                    </div>
                  </div>
                </Button>
              ))
            )}

            {!loadingList && conversations.length > 0 && (
              <div className="py-2 text-center text-xs text-agentic-text-secondary">
                {loadingMore ? (
                  'Loading more…'
                ) : loadMoreFailed ? (
                  <Button tone="custom" onClick={loadMore} className="underline hover:text-agentic-text-primary">
                    Couldn't load more. Retry
                  </Button>
                ) : !hasMore ? (
                  'No more conversations'
                ) : null}
              </div>
            )}
            <div ref={listBottomRef} className="h-1" />
          </div>

          <div className="mt-3">
            <Button tone="custom"
              onClick={() => loadList()}
              className="w-full px-3 py-2 rounded-lg text-sm border bg-white hover:bg-gray-50"
            >
              Refresh
            </Button>
          </div>
        </aside>

        {/* Chat pane */}
        <section className="col-span-10 md:col-span-7 bg-white rounded-lg border flex flex-col">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-agentic-text-primary">
                {selectedConversation?.title ?? 'Conversation details'}
              </div>
              <div className="text-xs text-agentic-text-secondary">
                {selectedConversation
                  ? `${selectedConversation.messageCount ?? 0} messages • ${formatDate(
                    selectedConversation.createdAt
                  )}`
                  : ''}
              </div>
            </div>
            <div>
              <div className="text-xs text-agentic-text-secondary">{chatbot?.name ?? ''}</div>
              <p className='text-xs text-agentic-text-secondary'>Average Coversation Response: {selectedConversation?.avgResponseTime ? `${convertMillisecondsToSeconds(selectedConversation.avgResponseTime)} (sec:ms)` : "-"}</p>
            </div>
          </div>

          <div className="h-[500px] p-4 overflow-y-auto space-y-4 bg-c-f7f9fc">
            {loadingConversation ? (
              <div className="h-full flex items-center justify-center text-agentic-text-secondary">
                Loading messages…
              </div>
            ) : !selectedConversation ? (
              <div className="h-full flex items-center justify-center text-agentic-text-secondary">
                {debouncedSearch && !loadingList && conversations.length === 0
                  ? 'No conversation found.'
                  : 'Select a conversation from the left to view messages.'}
              </div>
            ) : selectedConversation.messages?.length === 0 ? (
              <div className="h-full flex items-center justify-center text-agentic-text-secondary">
                No messages in this conversation.
              </div>
            ) : (
              <div className="space-y-3">
                {selectedConversation &&
                  (selectedConversation.messages ?? []).map((m: any, index: number) => {
                    const isUser = m.senderType === 'user';
                    const isAgent = m.senderType === 'agent';

                    const { isAssessment } = m.metadata || {};

                    const messageContent: string = m.metadata?.formattedMessage || m.message

                    const themeConfig = selectedConversation?.chatbot?.themeConfig?.settings ||
                      selectedConversation?.chatbot?.themeSettings?.settings ||
                      selectedConversation?.chatbot?.settings || {};
                    const theme: any = {
                      userMsgBg: themeConfig.userMessageBgColor || themeConfig.userMessageBg || `linear-gradient(90deg, ${cssColor('abcad9')} 0%, ${cssColor('5484c5')} 100%)`,
                      userMsgFont: themeConfig.userMessageFontColor || themeConfig.userMessageFont || '#ffffff',
                      botMsgBg: themeConfig.botMessageBgColor || themeConfig.botMsgBg || '#ffffff',
                      headerBgColor: themeConfig.headerBgColor || themeConfig.headerBgColor || '#ffffff',
                      botMsgFont: themeConfig.botMessageFontColor || themeConfig.botMsgFont || '#000000',
                      sendBtn: themeConfig.sendButtonColor || themeConfig.sendBtn || cssColor('3b82f6'),
                      bg: themeConfig.bgColor || themeConfig.bg || '#f0f6ff',
                      inputTextBg: themeConfig.inputTextBgColor || themeConfig.inputTextBg || '#ffffff',
                      inputTextFont: themeConfig.inputTextFontColor || themeConfig.inputTextFont || '#000000',
                      inputBorder: themeConfig.inputFieldBorderColor || themeConfig.inputBorder || cssColor('e5e7eb'),
                      fontFamily: themeConfig.fontFamily || 'Outfit, sans-serif',
                      welcome: themeConfig.welcomeMessage || themeConfig.welcome || 'Hi! How can I help you today?',
                      placeholder: themeConfig.placeholderText || themeConfig.placeholder || 'Type your message...',
                      headerTextFont: themeConfig.headerTextFont || "#fff"
                    }

                    return (
                      <div>
                        <div
                          key={m.id}
                          className={`relative flex items-center gap-[6px] ${isAssessment ? "" : "max-w-[78%] w-fit"} ${isUser ? 'ml-auto text-right' : ''}`}
                        >
                          <div className={`${isUser ? '' : 'w-full max-w-full overflow-hidden'}`}>
                            <div
                              className="w-min-full rounded-xl px-4 py-2.5 text-sm shadow-sm"
                              style={
                                isUser
                                  ? {
                                    background:
                                      theme?.userMsgBg ||
                                      `linear-gradient(90deg, ${cssColor('abcad9')} 0%, ${cssColor('5484c5')} 100%)`,
                                    color: theme?.userMsgFont || '#ffffff',
                                  }
                                  : {
                                    background: theme?.botMsgBg || '#ffffff',
                                    color: theme?.botMsgFont || '#000000',
                                    border: `1px solid ${cssColor('e5e7eb')}`,
                                  }
                              }
                            >
                              <div className="message-content space-y-6">
                                {isAssessment ? (
                                  <SymptomCheckerAssessmentMessageBubble
                                    metadata={m.metadata}
                                  />
                                ) : renderChatMarkdown(messageContent)}
                              </div>
                            </div>
                          </div>

                          {/* Audit icon for agent messages */}
                          {isAgent && (
                            <span
                              className="cursor-pointer text-agentic-text-secondary hover:text-agentic-accent-purple"
                              onClick={() => {
                                if (!m.hasAudits) {
                                  setSelectedAgents([m.agentInstanceId]);
                                  setShowAuditMenu(false);
                                  setShowAuditQuestion(true);
                                  openAuditMenu(m)
                                } else {
                                  toast.info("This message has already been audited.");
                                }
                              }}   // ⭐ NEW FUNCTION
                            >
                              {
                                m.hasAudits ? <Flag size={16} fill={cssColor('1e3a8a')} color={cssColor('1e3a8a')} /> : <Flag size={16} />
                              }

                            </span>
                          )}

                        </div>
                        <div className={`text-[11px] text-agentic-text-secondary mt-1 flex items-center gap-2 ${isUser ? 'justify-end' : ''}`}>
                          <span>
                            {new Intl.DateTimeFormat('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                              day: 'numeric',
                              month: 'short',
                            }).format(new Date(m.createdAt))}
                          </span>
                          {isAgent && <SourcesBadge sources={m.metadata?.sources} onOpen={() => setSourcesMessageId(m.id)} />}
                        </div>
                      </div>
                    );
                  })

                }
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Footer area kept minimal (no input / send) */}
          <div className="px-4 py-3 border-t text-xs text-agentic-text-secondary">
            Conversation history only — messages are read-only.
            {error && <div className="text-red-500 mt-1">{error}</div>}
          </div>
        </section>

        <SourcesDrawer
          open={Boolean(sourcesMessageId)}
          onClose={() => setSourcesMessageId(null)}
          sources={selectedConversation?.messages?.find((msg: any) => msg.id === sourcesMessageId)?.metadata?.sources}
          onRefresh={loadConversation}
        />

        {showAuditMenu && (
          <Modal
            open={showAuditMenu}
            onClose={() => {
              setShowAuditMenu(false);
              setSelectedAgents([]);
            }}
            title="Flag Response & Assign Agents"
            size="md"
            loading={loading}
          >
            {/* <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50"> */}
            <div>
              <h3 className="text-base font-semibold mb-3">Flag Response & Assign Agents</h3>

              <p className="text-xs text-gray-600 mb-3">
                Select one or more Agent Instances that should learn from this corrected response.
              </p>

              <div className="border rounded-lg max-h-60 overflow-y-auto p-2 space-y-2">
                {agentInstances.length === 0 && (
                  <div className="text-xs text-gray-500">No agent instances found.</div>
                )}

                {agentInstances.map((ai) => (
                  <label
                    key={ai.id}
                    className="flex items-center gap-3 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <Checkbox
                     
                      checked={selectedAgents.includes(ai.id)}
                      onChange={() => {
                        setSelectedAgents((prev) =>
                          prev.includes(ai.id)
                            ? prev.filter((x) => x !== ai.id)
                            : [...prev, ai.id]
                        );
                      }}
                    />
                    <div>
                      <div className="font-medium text-sm">{ai.name}</div>
                      <div className="text-xs text-gray-500">{ai.id}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Footer actions */}
              <div className="flex justify-between mt-5">
                <Button tone="custom"
                  className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                  onClick={() => {
                    setSelectedAgents([]);
                    setShowAuditMenu(false);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>

                <Button tone="custom"
                  // onClick={submitMultiAudit}
                  onClick={() => {
                    setShowAuditMenu(false);
                    setShowAuditQuestion(true);
                  }}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${selectedAgents.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  disabled={selectedAgents.length === 0 || loading}
                >
                  Submit Audit ({selectedAgents.length})
                </Button>
              </div>
            </div>
            {/* </div> */}
          </Modal>

        )}

        {
          showAuditQuestion &&
          audit &&
          <Modal
            open={showAuditQuestion && Boolean(audit)}
            onClose={() => {
              setShowAuditQuestion(false);
              setAudit({});
            }}
            title="Flag Response & Provide Correct Answer"
            size="md"
            footer={
              <div className="flex justify-end gap-2">
                <Button tone="custom"
                  className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                  onClick={() => {
                    setShowAuditQuestion(false);
                    setAudit({});
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>

                <Button tone="custom"
                  onClick={() => submitMultiAudit(false)}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${!audit.correctedAnswer
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  disabled={!audit.correctedAnswer || loading}
                >
                  Move to QA
                </Button>
                <Button tone="custom"
                  onClick={() => submitMultiAudit(true)}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${!audit.correctedAnswer
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  disabled={!audit.correctedAnswer || loading}
                >
                  Move to QA & Train
                </Button>
              </div>
            }
          >
            <div>
              {/* Question */}
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  User Question
                </label>
                <TextArea tone="muted"
                  className="w-full mt-1 border rounded-lg p-2 text-sm"
                  readOnly
                  rows={2}
                  value={auditUserMessage?.message || ''}
                />
              </div>

              {/* Answer (Editable) */}
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Bot Answer
                </label>
                <TextArea tone="muted"
                  className="w-full mt-1 border rounded-lg p-2 text-sm"
                  rows={4}
                  value={auditTargetMessage?.message || ''}
                  readOnly
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Correct Bot Answer (Edit below)
                </label>
                <TextArea
                  className="w-full mt-1 border rounded-lg p-2 text-sm"
                  rows={4}
                  value={audit?.correctedAnswer || ''}
                  onChange={(e) => {
                    setAudit((prev: any) => ({ ...prev, correctedAnswer: e.target.value }));
                  }}
                />
              </div>

              {/* <div className="flex justify-end">
                <button
                  // disabled={savingId === audit.id}
                  // onClick={() => moveToQA(audit)}
                  onClick={submitMultiAudit}
                  className={`px-5 py-2.5 rounded-lg text-white text-sm font-medium transition-all `}
                  // ${savingId === audit.id
                  //     ? "bg-green-400 cursor-not-allowed"
                  //     : "bg-green-600 hover:bg-green-700"
                  //   }`}
                >
                  Move to QA & Train
                </button>
              </div> */}
            </div>
          </Modal>

        }


      </div>
    </div>
  );
};

export default ConversationsTab;
